import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, X } from "lucide-react";
import { Button } from "./Button";
import { useTranslation } from "react-i18next";

interface InformationDialogProps {
	isOpen: boolean;
	onClose: () => void;
}

const PUBLIC_SITE_BASE = process.env.REACT_APP_APPLE_REDIRECT_URI ?? "https://kokobeo.com";

const resolveInformationPath = (language: string) => {
	const normalized = language && language.length === 2 ? language : "en";
	return normalized === "en" ? "/en/information/contract" : `/${normalized}/information/contract`;
};

const InformationDialog: React.FC<InformationDialogProps> = ({ isOpen, onClose }) => {
	const { t, i18n } = useTranslation();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [iframeSrc, setIframeSrc] = useState<string>(`${PUBLIC_SITE_BASE}/information`);

	useEffect(() => {
		if (!isOpen) return;
		const lang = (i18n.language || "en").split("-")[0];
		const route = resolveInformationPath(lang);
		setIframeSrc(`${PUBLIC_SITE_BASE}${route}`);
		setIsLoading(true);
	}, [i18n.language, isOpen]);

	useEffect(() => {
		if (isOpen) {
			setIsLoading(true);
		}
	}, [isOpen]);

	const handleIframeLoad = () => {
		setIsLoading(false);
	};

	if (!isOpen) return null;

	return (
		<AnimatePresence>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				className="fixed inset-0 bg-black bg-opacity-50 z-[10005] flex items-center justify-center p-4"
				onClick={(e) => {
					if (e.target === e.currentTarget) onClose();
				}}
			>
				<motion.div
					initial={{ scale: 0.95, y: 20 }}
					animate={{ scale: 1, y: 0 }}
					exit={{ scale: 0.95, y: 20 }}
					className="bg-white rounded-xl w-full max-w-5xl h-full max-h-[90vh] flex flex-col shadow-xl"
					onClick={(e) => e.stopPropagation()}
				>
					<div className="flex items-center justify-between border-b px-6 py-4">
						<div>
							<h2 className="text-xl font-bold text-gray-900">{t("informationDialog.title")}</h2>
							<p className="text-gray-600 mt-1 text-sm">{t("informationDialog.subtitle")}</p>
						</div>
						<button
							onClick={onClose}
							className="p-2 hover:bg-gray-100 rounded-full transition-colors"
							aria-label={t("informationDialog.close", "Close")}
						>
							<X className="h-5 w-5 text-gray-500" />
						</button>
					</div>

					<div className="flex-1 relative bg-gray-100">
						{isLoading && (
							<div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
								<Loader2 className="h-5 w-5 animate-spin text-gray-400" />
								<span className="mt-2 text-xs text-gray-500">{t("informationDialog.loading", "Loading information...")}</span>
							</div>
						)}

						<iframe
							key={iframeSrc}
							title="Contract information"
							src={iframeSrc}
							className="h-full w-full border-0 rounded-b-xl"
							onLoad={handleIframeLoad}
						/>
					</div>

					<div className="flex justify-end border-t px-6 py-4 bg-white">
						<Button type="button" onClick={onClose} className="px-6">
							{t("informationDialog.close")}
						</Button>
					</div>
				</motion.div>
			</motion.div>
		</AnimatePresence>
	);
};

export default InformationDialog;
